// src/pages/task/TaskTypeEditPage.tsx
// Edit a task type — the same house-style cards as the add page, prefilled,
// with the slug locked, plus the lifecycle editors this type already has.
// No page-level breadcrumb: the app top bar already carries one.

import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useTaskCustomField, useTaskTaskType, useI18n } from '@react/hooks';
import { Button, Card, CardContent, CardHeader, CardTitle, CardDescription, ErrorAlert } from '@react/components';
import {
  TaskTypeForm,
  taskTypeFormDefaults,
  assignmentRoleMissing,
  normalizeAssignmentRule,
  requiresAssignmentRole,
  type TaskTypeFormData,
} from '@/features/task/TaskTypeForm';
import { TaskTypeFormPropertiesEditor } from '@/features/task/TaskTypeFormPropertiesEditor';
import { TaskTypeStatusesEditor } from '@/features/task/TaskTypeStatusesEditor';
import { TaskTypeTransitionsEditor } from '@/features/task/TaskTypeTransitionsEditor';
import { useResolvedTaskConfig } from '@/features/task/useResolvedTaskConfig';
import { inheritedReference, inheritedStatusSet } from '@/features/task/inheritance';
import { durationToSeconds, secondsToDuration } from '@/features/task/panelHelpers';

// TaskTypeDefinition field paths touched by the form (maps 1:1 to the request
// field_mask, except the derived default_assignee/default_estimated_duration
// which are computed from default_assignee_id/name and default_estimated_hours).
const EDITABLE_PATHS = [
  'display_name', 'description', 'source_entity_type', 'default_priority',
  'initial_status', 'completion_status', 'enable_time_tracking', 'enable_checklists',
  'enable_dependencies', 'enable_progress', 'enable_watchers', 'requires_assignee',
  'icon', 'color', 'default_assignee', 'default_estimated_duration', 'default_tags',
  'preset', 'is_active', 'enable_custom_fields', 'custom_field_ids',
  // What the type's forms ask for. Cleared back to empty means "follow the
  // platform default set again", so the path has to travel even when the list
  // is empty — an omitted path would leave the old list on the record.
  'form_properties',
  // Both inherit when cleared, so both must be in the mask: an omitted path
  // would leave a stale value on the record and the form would keep promising
  // inheritance that never happens.
  'default_pipeline', 'default_template',
  // Who a new task goes to before default_assignee is consulted, and the role
  // the role rules point at. Both must be listed or an edit is dropped silently.
  'assignment_rule', 'assignment_role',
  // The lifecycle: the states this type has, and the graph it moves along.
  'status_definitions', 'transitions',
];

/**
 * Task type edit page.
 * Allows admins to update an existing task type definition.
 */
export const TaskTypeEditPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useTaskTaskType();
  // The form-properties editor names custom fields rather than showing raw ids;
  // TaskTypeForm already loads the catalogue, so this reads the same store.
  const customFieldStore = useTaskCustomField();
  const { t } = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [formData, setFormData] = useState<TaskTypeFormData>(taskTypeFormDefaults());

  // What this type's scope offers on its own — the statuses, template and
  // pipeline a type with none of its own follows, and the ones a type with its
  // own replaces. Resolved once for the page and read by both editors.
  const resolved = useResolvedTaskConfig();

  useEffect(() => {
    if (id) {
      store.GetTaskType(id);
    }
    return () => {
      store.clearCurrent();
    };
  }, [id]);

  useEffect(() => {
    if (store.currentItem) {
      const item = store.currentItem as any;
      const estimatedSeconds = durationToSeconds(item.default_estimated_duration);
      setFormData({
        ...taskTypeFormDefaults(),
        ...item,
        default_assignee_id: item.default_assignee?.entity?.id || item.default_assignee?.id || '',
        default_assignee_name: item.default_assignee?.display_name || '',
        assignment_rule: normalizeAssignmentRule(item.assignment_rule),
        assignment_role_id: item.assignment_role?.id || '',
        default_estimated_hours: estimatedSeconds > 0 ? String(estimatedSeconds / 3600) : '',
        default_tags: item.default_tags || [],
        preset: item.preset || 'TASK_TYPE_PRESET_STANDARD',
        is_active: item.is_active ?? true,
        enable_custom_fields: item.enable_custom_fields ?? false,
        custom_field_ids: item.custom_field_ids || [],
        form_properties: item.form_properties || [],
        default_pipeline_id: item.default_pipeline?.id || '',
        default_template_id: item.default_template?.id || '',
        status_definitions: item.status_definitions || [],
        transitions: item.transitions || [],
      });
    }
  }, [store.currentItem]);

  const customFieldOptions = customFieldStore.items.map((def) => ({
    value: def.entity?.id || def.slug,
    label: def.display_name,
  }));

  const handleChange = <K extends keyof TaskTypeFormData>(field: K, value: TaskTypeFormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!id || !store.currentItem) return;

    // The backend refuses a role rule with no role. Say which field is missing,
    // in place, rather than sending a request that comes back as a banner.
    setSubmitAttempted(true);
    if (assignmentRoleMissing(formData)) return;

    setIsSubmitting(true);
    try {
      const hours = parseFloat(formData.default_estimated_hours);
      await store.UpdateTaskType(id, {
        definition: {
          ...store.currentItem,
          ...formData,
          default_assignee: formData.default_assignee_id
            ? { entity: { id: formData.default_assignee_id }, display_name: formData.default_assignee_name }
            : undefined,
          default_estimated_duration: hours > 0 ? secondsToDuration(hours * 3600) : undefined,
          // Cleared here as well as in the form, so a rule that no longer reads
          // a role never carries the old one back to the server.
          assignment_role: requiresAssignmentRole(formData.assignment_rule) && formData.assignment_role_id
            ? { id: formData.assignment_role_id, type_name: 'role' }
            : undefined,
          // Cleared means "follow the scope again", so an empty id must travel
          // as an absent reference rather than as the previous one.
          default_pipeline: formData.default_pipeline_id ? { id: formData.default_pipeline_id } : undefined,
          default_template: formData.default_template_id ? { id: formData.default_template_id } : undefined,
        },
        field_mask: EDITABLE_PATHS,
      } as never);
      navigate('/settings/task-types');
    } catch {
      // Error is handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  if (store.isLoading) {
    return (
      <div className="mx-auto max-w-3xl space-y-6">
        <div className="animate-pulse">
          <div className="mb-2 h-7 w-56 rounded bg-muted" />
          <div className="h-4 w-80 rounded bg-muted" />
        </div>
        <div className="space-y-4 rounded-xl border border-border p-6">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="mb-2 h-3 w-20 rounded bg-muted" />
              <div className="h-9 w-full rounded bg-muted" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">{t('task.editType')}</h1>
        <p className="text-sm text-muted-foreground">{t('task.editTypeDescription')}</p>
      </div>

      {!!store.error && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      <TaskTypeForm
        value={formData}
        onChange={handleChange}
        isEdit
        submitAttempted={submitAttempted}
        inheritedTemplate={inheritedReference(resolved.data?.template, id)}
        inheritedTemplateName={resolved.data?.templateName || ''}
        inheritedPipeline={inheritedReference(resolved.data?.pipeline, id)}
        inheritedPipelineName={resolved.data?.pipelineName || ''}
        askedScope={resolved.scopeEntity}
        isResolving={resolved.isLoading}
        resolveError={resolved.error}
        onRetryResolve={resolved.reload}
      />

      <Card>
        <CardHeader>
          <CardTitle>{t('task.formProperties.title')}</CardTitle>
          <CardDescription>{t('task.formProperties.cardDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <TaskTypeFormPropertiesEditor
            value={formData.form_properties}
            onChange={(next) => handleChange('form_properties', next)}
            customFieldOptions={customFieldOptions}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('task.lifecycle.statuses.title')}</CardTitle>
          <CardDescription>{t('task.typeForm.sections.statuses.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <TaskTypeStatusesEditor
            value={formData.status_definitions}
            onChange={(next) => handleChange('status_definitions', next)}
            inherited={inheritedStatusSet(resolved.data?.statuses, id)}
            askedScope={resolved.scopeEntity}
            isResolving={resolved.isLoading}
            resolveError={resolved.error}
            onRetryResolve={resolved.reload}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('task.lifecycle.transitions.title')}</CardTitle>
          <CardDescription>{t('task.typeForm.sections.transitions.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <TaskTypeTransitionsEditor
            value={formData.transitions}
            onChange={(next) => handleChange('transitions', next)}
            statusDefinitions={formData.status_definitions}
            initialStatus={formData.initial_status}
            completionStatus={formData.completion_status}
            customFieldIds={formData.custom_field_ids}
            enableCustomFields={formData.enable_custom_fields}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex items-center justify-end gap-3">
          <Link to="/settings/task-types">
            <Button type="button" variant="secondary">
              {t('common.cancel')}
            </Button>
          </Link>
          <Button type="button" onClick={handleSubmit} disabled={isSubmitting} isLoading={isSubmitting}>
            {t('common.saveChanges')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
});

export default TaskTypeEditPage;
